import type { CityConfig } from "../city-template/types";

export const menifeeConfig: CityConfig = {
  city: "Menifee",
  state: "California",
  stateAbbr: "CA",
  slug: "menifee",
  route: "/cities/menifee",
  title: "Emergency Passport Menifee, CA | Fast Passport Renewal & Expedited Help",
  metaDescription: "Emergency passport and expedited passport services in Menifee, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Riverside County and Inland Empire travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Menifee, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Menifee and Inland Empire travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Menifee%20California%20suburban%20cityscape%20at%20golden%20hour%20with%20rolling%20hills%20and%20modern%20residential%20developments%2C%20Lake%20Menifee%20in%20the%20background%2C%20warm%20sunset%20light%20over%20the%20Inland%20Empire%20landscape%2C%20professional%20landscape%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20serene%20suburban%20community%20with%20mountain%20backdrop%20and%20clean%20modern%20environment&width=1800&height=700&seq=menifee-hero-001&orientation=landscape",
  heroImageAlt: "Menifee California suburban landscape at golden hour with rolling hills and Lake Menifee",
  geoPosition: "33.6905;-117.1859",
  geoPlacename: "Menifee, California",
  regionalAgency: {
    name: "San Diego Passport Agency",
    address: "401 W A St, San Diego, CA 92101",
    distance: "Located in downtown San Diego — approximately 60–75 minutes from Menifee via I-15",
  },
  nearbyCitiesZips: [
    { name: "Menifee", zips: "92584, 92585, 92586, 92587" },
    { name: "Murrieta", zips: "92563, 92564" },
    { name: "Temecula", zips: "92589, 92590, 92591, 92592, 92593" },
    { name: "Hemet", zips: "92543, 92544, 92545, 92546" },
    { name: "Lake Elsinore", zips: "92530, 92531, 92532" },
    { name: "Perris", zips: "92570, 92571, 92572" },
    { name: "Wildomar", zips: "92595" },
    { name: "Canyon Lake", zips: "92587" },
    { name: "Sun City", zips: "92585, 92586" },
    { name: "Riverside", zips: "92501–92509, 92513–92519, 92521" },
    { name: "Moreno Valley", zips: "92551, 92552, 92553, 92555, 92557" },
    { name: "Corona", zips: "92877, 92878, 92879, 92880, 92881, 92882, 92883" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Inland Empire departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Los Angeles", route: "/cities/los-angeles" },
    { name: "San Diego", route: "/cities/san-diego" },
    { name: "Riverside", route: "/cities/riverside" },
    { name: "Moreno Valley", route: "/cities/moreno-valley" },
    { name: "San Bernardino", route: "/cities/san-bernardino" },
    { name: "Oceanside", route: "/cities/oceanside" },
    { name: "Ontario", route: "/cities/ontario" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Menifee remotely via secure courier. We do not have a walk-in office in Menifee — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Inland Empire travelers.",
  fpcOfficeDistance: "Serving all of Riverside County",
  serviceAreaIntro: "From the master-planned communities along the Menifee Valley to the Sun City retirement area, Fast Passport Center serves the entire Menifee region. We regularly help travelers from Murrieta, Temecula, Hemet, and Lake Elsinore get their passports fast.",
  nearbyCitiesIntro: "We serve all Menifee neighborhoods and nearby Inland Empire cities including Murrieta, Temecula, Hemet, Lake Elsinore, Perris, and Wildomar. If you are in southwestern Riverside County, we have you covered.",
  howItWorksIntro: "Most Menifee travelers complete everything online and ship documents overnight. No need to drive to San Diego or Los Angeles or wait in line at the regional agency.",
  mistakesIntro: "With SNA and SAN serving as the closest international airports, Menifee travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Inland Empire and Temecula Valley travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Menifee? Here are the most common ones we hear from Riverside County travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Inland Empire international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Carlos R.",
      location: "Menifee, CA",
      rating: 5,
      text: "My flight to Cancun was in 3 days and my passport was expired. Fast Passport Center got me a new one in 36 hours. Saved our family vacation completely.",
      service: "Emergency Passport",
    },
    {
      name: "Amanda S.",
      location: "Murrieta, CA",
      rating: 5,
      text: "Needed to renew for a business trip to London. The process was incredibly smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "James D.",
      location: "Temecula, CA",
      rating: 5,
      text: "Lost my passport a week before my honeymoon to Hawaii. They walked me through the DS-64 process and got me a replacement in 48 hours. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Patricia L.",
      location: "Hemet, CA",
      rating: 5,
      text: "As a frequent international traveler from the Inland Empire, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "menifee-1",
      name: "Menifee Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "29823 Haun Rd",
      city: "Menifee",
      state: "CA",
      zip: "92584",
      phone: "(951) 672-9244",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.5 miles from downtown Menifee",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "menifee-2",
      name: "Murrieta Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "25309 Madison Ave",
      city: "Murrieta",
      state: "CA",
      zip: "92563",
      phone: "(951) 698-6204",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "6.2 miles from Menifee",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "menifee-3",
      name: "Temecula Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "28471 Rancho California Rd",
      city: "Temecula",
      state: "CA",
      zip: "92590",
      phone: "(951) 676-3254",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "8.5 miles from Menifee",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for Temecula Valley residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "menifee-4",
      name: "Perris Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "300 N Perris Blvd",
      city: "Perris",
      state: "CA",
      zip: "92570",
      phone: "(951) 657-2254",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "12 miles from Menifee",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for northern Riverside County. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "menifee-5",
      name: "Hemet Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "3340 W Florida Ave",
      city: "Hemet",
      state: "CA",
      zip: "92545",
      phone: "(951) 658-3114",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "15 miles from Menifee",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for San Jacinto Valley residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "menifee-6",
      name: "Riverside County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "2720 Gateway Dr",
      city: "Riverside",
      state: "CA",
      zip: "92507",
      phone: "(951) 486-7000",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "20 miles from Menifee",
      website: "https://www.rivco.org/",
      websiteLabel: "Riverside County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown San Diego", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Inland Empire photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Menifee acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Menifee applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the San Diego regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Inland Empire families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Menifee travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Menifee." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Menifee address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Menifee before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Menifee for travelers with urgent international flights. Our registered couriers achieve 24–48 hour turnaround for most Inland Empire travelers." },
    { q: "How fast can I renew my passport in Menifee?", a: "Fast Passport Center offers fast passport renewal in Menifee in as little as 24–48 hours through our emergency courier service." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. We can expedite your passport renewal in Menifee in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Menifee?", a: "Same-day passport appointments are only available at the LA Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Menifee residents." },
    { q: "Can I expedite a child passport in Menifee?", a: "Yes. Fast Passport Center expedites child passports in Menifee for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Menifee with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Menifee?",
      a: "With Fast Passport Center's emergency courier service, Menifee travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to the San Diego Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Menifee?",
      a: "Popular options include the Menifee Post Office (92584), Murrieta Post Office (92563), Temecula Post Office (92590), and Perris Post Office (92570). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Menifee?",
      a: "Same-day passports are only available at the San Diego Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in Sun City or Canyon Lake?",
      a: "We serve all of southwestern Riverside County including Sun City, Canyon Lake, Wildomar, and Quail Valley. Our overnight shipping labels work from any UPS location, so your location within the Menifee area doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Menifee?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Menifee?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Menifee, Murrieta, and Temecula offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Menifee?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Menifee?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Inland Empire families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Menifee", address: "30206 Haun Rd, Menifee, CA 92584", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Murrieta", address: "25125 Madison Ave, Murrieta, CA 92563", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Temecula", address: "27520 Ynez Rd, Temecula, CA 92591", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Menifee", address: "29780 Bradley Rd, Sun City, CA 92586", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Hemet", address: "2200 W Florida Ave, Hemet, CA 92545", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Lake Elsinore", address: "31581 Canyon Estates Dr, Lake Elsinore, CA 92532", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Murrieta", address: "40477 Murrieta Hot Springs Rd, Murrieta, CA 92563", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Temecula", address: "26610 Ynez Rd, Temecula, CA 92591", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Menifee",
      address: "30290 Haun Rd, Menifee, CA 92584",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Murrieta",
      address: "25225 Madison Ave, Murrieta, CA 92563",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Temecula",
      address: "28965 Temecula Pkwy, Temecula, CA 92590",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Hemet",
      address: "1101 S State St, Hemet, CA 92543",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for San Jacinto Valley residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "SNA",
      name: "John Wayne Airport (Orange County)",
      distance: "35 miles from Menifee via I-15",
      address: "18601 Airport Way, Santa Ana, CA 92707",
      note: "The closest major international airport for Menifee. International flights to Mexico, Canada, and connections to Europe.",
      flightUrl: "https://www.ocair.com/",
      flightLabel: "John Wayne Airport",
      featured: true,
    },
    {
      code: "SAN",
      name: "San Diego International Airport",
      distance: "60 miles from Menifee via I-15",
      address: "3225 N Harbor Dr, San Diego, CA 92101",
      note: "Major international hub for long-haul flights to Asia, Europe, and Latin America.",
      flightUrl: "https://www.san.org/",
      flightLabel: "San Diego Airport",
      featured: false,
    },
    {
      code: "ONT",
      name: "Ontario International Airport",
      distance: "40 miles from Menifee via I-15",
      address: "2900 E Airport Dr, Ontario, CA 91761",
      note: "Growing international airport with flights to Mexico and domestic connections. Less crowded than LAX.",
      flightUrl: "https://www.flyontario.com/",
      flightLabel: "Ontario Airport",
      featured: false,
    },
  ],
};